import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { contractsAPI, vehiclesAPI, driversAPI, usersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Search,
  MapPin,
  Clock,
  Users,
  DollarSign,
  Circle,
  Eye,
  FileText,
  Filter
} from "lucide-react";
import { cn } from "../lib/utils";

export default function Contratos() {
  const { isLogistica } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [trafficFilter, setTrafficFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [salesOwnerFilter, setSalesOwnerFilter] = useState("all");
  const [salesUsers, setSalesUsers] = useState([]);

  const trafficLightColors = {
    rojo: { bg: "bg-red-100", text: "text-red-800", border: "border-red-200", dot: "bg-red-500" },
    amarillo: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200", dot: "bg-yellow-500" },
    verde: { bg: "bg-green-100", text: "text-green-800", border: "border-green-200", dot: "bg-green-500" }
  };

  const statusLabels = {
    solicitud: "Solicitud",
    cotizada: "Cotizada",
    contrato: "Contrato",
    asignada: "Asignada",
    en_curso: "En Curso",
    completado: "Completado",
    cancelado: "Cancelado",
    no_show: "No Show"
  };

  const serviceTypes = {
    transfer: "Transfer",
    aeropuerto: "Aeropuerto",
    hotel: "Hotel",
    disposicion: "Disposición"
  };

  useEffect(() => {
    fetchContracts();
    fetchSalesUsers();
  }, [trafficFilter, statusFilter]);

  // v4.65.0: búsqueda server-side (debounced) para garantizar encontrar cualquier contrato
  // (ej. folio 2026-1049) aunque el dataset sea grande.
  useEffect(() => {
    const t = setTimeout(() => { fetchContracts(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchSalesUsers = async () => {
    try {
      const response = await usersAPI.getVendedores();
      setSalesUsers(response.data || []);
    } catch (error) {
      console.error("Error loading sales users:", error);
    }
  };

  const fetchContracts = async () => {
    try {
      const params = {};
      if (trafficFilter !== "all") params.traffic_light = trafficFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      // El filtro de sales_owner se aplicará en frontend para flexibilidad
      
      const response = await contractsAPI.getAll(params);
      setContracts(response.data);
    } catch (error) {
      toast.error("Error al cargar contratos");
    } finally {
      setLoading(false);
    }
  };

  const filteredContracts = contracts.filter(contract => {
    // Filtro por vendedor asignado (sales_owner_id)
    if (salesOwnerFilter !== "all") {
      if (salesOwnerFilter === "none") {
        // Mostrar solo contratos sin vendedor asignado
        // Check both sales_owner_id and vendor_id for legacy contracts
        if (contract.sales_owner_id || contract.vendor_id) return false;
      } else {
        // Filtrar por vendedor específico - compare with sales_owner_id
        const contractOwnerId = contract.sales_owner_id || contract.vendor_id;
        if (contractOwnerId !== salesOwnerFilter) return false;
      }
    }
    
    // Filtro por búsqueda de texto (incluye folio)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        contract.folio?.toLowerCase().includes(search) ||
        contract.client_name?.toLowerCase().includes(search) ||
        contract.client_phone?.includes(search) ||
        contract.origin?.toLowerCase().includes(search) ||
        contract.destination?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="contratos-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Contratos</h1>
            <p className="text-slate-500 mt-1">Gestiona los contratos de servicio</p>
          </div>
          <div className="flex gap-2">
            <Link to="/cotizaciones">
              <Button variant="outline" className="gap-2" data-testid="new-quote-btn">
                <Plus className="w-4 h-4" />
                Desde Cotización
              </Button>
            </Link>
            <Link to="/contratos/nuevo">
              <Button className="gap-2" data-testid="new-contract-btn">
                <Plus className="w-4 h-4" />
                Contrato Directo
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por cliente, teléfono o ruta..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Select value={trafficFilter} onValueChange={setTrafficFilter}>
                  <SelectTrigger className="w-40" data-testid="traffic-filter">
                    <SelectValue placeholder="Semáforo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="rojo">🔴 Rojo</SelectItem>
                    <SelectItem value="amarillo">🟡 Amarillo</SelectItem>
                    <SelectItem value="verde">🟢 Verde</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40" data-testid="status-filter">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="contrato">Contrato</SelectItem>
                    <SelectItem value="asignada">Asignada</SelectItem>
                    <SelectItem value="en_curso">En Curso</SelectItem>
                    <SelectItem value="completado">Completado</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={salesOwnerFilter} onValueChange={setSalesOwnerFilter}>
                  <SelectTrigger className="w-48" data-testid="sales-owner-filter">
                    <SelectValue placeholder="Vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los vendedores</SelectItem>
                    <SelectItem value="none">Sin vendedor asignado</SelectItem>
                    {salesUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name || user.email}{user.is_active === false ? " (inactivo)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div>
                  <p className="text-2xl font-bold text-red-600">
                    {contracts.filter(c => c.traffic_light === "rojo").length}
                  </p>
                  <p className="text-sm text-slate-500">Sin asignar</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">
                    {contracts.filter(c => c.traffic_light === "amarillo").length}
                  </p>
                  <p className="text-sm text-slate-500">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {contracts.filter(c => c.traffic_light === "verde").length}
                  </p>
                  <p className="text-sm text-slate-500">Confirmados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-2xl font-bold text-slate-900">{contracts.length}</p>
                  <p className="text-sm text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : filteredContracts.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay contratos</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <span className="sr-only">Semáforo</span>
                      </TableHead>
                      <TableHead>Folio</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Ruta</TableHead>
                      <TableHead>Fecha/Hora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Pasajeros</TableHead>
                      {!isLogistica && <TableHead>Precio</TableHead>}
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredContracts.map((contract) => {
                      const currencySymbol = contract.currency === "USD" ? "USD $" : "$";
                      return (
                      <TableRow key={contract.id} data-testid={`contract-row-${contract.id}`}>
                        <TableCell>
                          <div className={cn(
                            "w-3 h-3 rounded-full",
                            trafficLightColors[contract.traffic_light]?.dot
                          )} title={contract.traffic_light?.toUpperCase()} />
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-slate-600" data-testid={`contract-folio-${contract.id}`}>
                            {contract.folio || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{contract.client_name}</p>
                            <p className="text-sm text-slate-500">{contract.client_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 max-w-xs">
                            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="text-sm truncate">{contract.origin} → {contract.destination}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">
                              {new Date(contract.pickup_datetime).toLocaleDateString("es-MX", {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{serviceTypes[contract.service_type]}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span>{contract.passengers}</span>
                          </div>
                        </TableCell>
                        {!isLogistica && (
                          <TableCell>
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1 font-medium">
                                <DollarSign className="w-4 h-4 text-green-600" />
                                <span>{currencySymbol}{contract.total_price?.toLocaleString("es-MX")}</span>
                              </div>
                              {contract.currency === "USD" && contract.total_amount_mxn && (
                                <span className="text-xs text-slate-500">
                                  MXN ${contract.total_amount_mxn?.toLocaleString("es-MX")}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge className={cn(
                            "text-xs",
                            contract.status === "completado" && "bg-green-100 text-green-800",
                            contract.status === "cancelado" && "bg-red-100 text-red-800",
                            contract.status === "en_curso" && "bg-blue-100 text-blue-800",
                            !["completado", "cancelado", "en_curso"].includes(contract.status) && "bg-slate-100 text-slate-800"
                          )}>
                            {statusLabels[contract.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Link to={`/contratos/${contract.id}`}>
                            <Button variant="ghost" size="sm" className="gap-1" data-testid={`view-contract-${contract.id}`}>
                              <Eye className="w-4 h-4" />
                              Ver
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
